import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';

/**
 * CRUD e import/export en bloque de vías para el panel (HU-009-B) — distinto
 * de `useVias.js` (solo lectura, lo usa el wizard ciudadano para el catálogo
 * público). Mismo patrón que `useUsuarios.js`/`usePermisos.js`: siempre
 * manda el JWT del personal logueado, la API ya responde 401/403 sin token
 * real o sin el permiso `vias.*` correspondiente.
 */
const useViasAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = (extra = {}) => ({
    Authorization: `Bearer ${tokenManager.getAccessToken()}`,
    ...extra,
  });

  const manejarRespuesta = async (response) => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const erroresDeCampo = Object.entries(data)
        .filter(([campo]) => !['error', 'detail', 'details', 'success'].includes(campo))
        .map(([campo, mensajes]) => `${campo}: ${Array.isArray(mensajes) ? mensajes.join(', ') : mensajes}`)
        .join(' — ');
      throw new Error(data.error || data.detail || erroresDeCampo || 'Error inesperado del servidor');
    }
    return data;
  };

  const descargarBlob = async (url, nombreArchivo) => {
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'No se pudo descargar el archivo');
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(objectUrl);
    document.body.removeChild(a);
  };

  const listarVias = async ({ search = '', tipo = '' } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (tipo) params.set('tipo', tipo);
      const url = params.toString() ? `${API_CIFRA.via.listar}?${params}` : API_CIFRA.via.listar;
      const response = await fetch(url, { headers: authHeaders() });
      const data = await manejarRespuesta(response);
      return { success: true, vias: data.vias || [] };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const crearVia = async (datos) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.via.crear, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datos),
      });
      const data = await manejarRespuesta(response);
      return { success: true, via: data.data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const actualizarVia = async (id, datos) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.via.actualizar(id), {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(datos),
      });
      const data = await manejarRespuesta(response);
      return { success: true, via: data.data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const eliminarVia = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_CIFRA.via.eliminar(id), {
        method: 'DELETE',
        headers: authHeaders(),
      });
      await manejarRespuesta(response);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const exportarExcel = async () => {
    setError(null);
    try {
      await descargarBlob(API_CIFRA.via.exportarExcel, 'vias.xlsx');
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const exportarJson = async () => {
    setError(null);
    try {
      await descargarBlob(API_CIFRA.via.exportarJson, 'vias.json');
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const importar = async (archivo, formato) => {
    setLoading(true);
    setError(null);
    try {
      const url = formato === 'json' ? API_CIFRA.via.importarJson : API_CIFRA.via.importarExcel;
      const formData = new FormData();
      formData.append('archivo', archivo);
      const response = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: formData,
      });
      const data = await manejarRespuesta(response);
      return { success: true, resultado: data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    listarVias,
    crearVia,
    actualizarVia,
    eliminarVia,
    exportarExcel,
    exportarJson,
    importar,
  };
};

export default useViasAdmin;
